/**
 * Search API Client
 * Thin wrapper for the search-service global search endpoint (port 3009, routed via Kong)
 */

const SEARCH_API_BASE = '/api/search';
const SEARCH_TIMEOUT = 10000;

// --- Types ---

export interface SearchHit<T> {
  document: T;
  highlights: Array<{
    field: string;
    snippet: string;
  }>;
}

export interface SearchResult<T> {
  hits: SearchHit<T>[];
  found: number;
  page: number;
  search_time_ms: number;
}

export interface DocumentHit {
  id: string;
  title: string;
  content: string;
  project_id: string;
  project_name?: string;
  doc_type?: string;
  status?: string;
  tags?: string[];
  created_at: number;
  updated_at?: number;
}

export interface UserHit {
  id: string;
  display_name: string;
  email: string;
  workspace_id?: string;
  role?: string;
  avatar_url?: string;
  created_at?: number;
}

export interface IssueHit {
  id: string;
  title: string;
  description: string;
  project_id: string;
  project_name?: string;
  type?: string;
  status?: string;
  priority?: string;
  board_id?: string;
  assignee?: string;
  labels?: string[];
  created_at?: number;
  updated_at?: number;
}

export interface SprintHit {
  id: string;
  name: string;
  project_id: string;
  project_name?: string;
  status?: string;
  epic_count?: number;
  epic_ids?: string[];
  batch_id?: string;
  created_at: number;
  started_at?: number;
  completed_at?: number;
}

export interface EmailThreadHit {
  id: string;
  subject: string;
  snippet?: string;
  from_address?: string;
  to_addresses?: string[];
  participants?: string[];
  message_count?: number;
  labels?: string[];
  last_message_at?: number;
  created_at: number;
}

export interface TopicHit {
  id: string;
  title: string;
  description?: string;
  status?: string;
  workspace_id?: string;
  assigned_agents?: string[];
  item_count?: number;
  created_at: number;
  updated_at?: number;
}

export interface GlobalSearchResponse {
  documents: SearchResult<DocumentHit>;
  users: SearchResult<UserHit>;
  issues: SearchResult<IssueHit>;
  sprints: SearchResult<SprintHit>;
  emails: SearchResult<EmailThreadHit>;
  topics: SearchResult<TopicHit>;
}

export interface RecentItemsResponse {
  documents: SearchResult<DocumentHit>;
  issues: SearchResult<IssueHit>;
  topics: SearchResult<TopicHit>;
}

// --- Client ---

export const searchApi = {
  /**
   * Fetch recently updated items (documents, issues, topics)
   * GET /api/search/recents
   */
  recents: async (workspaceId?: string | null, signal?: AbortSignal): Promise<RecentItemsResponse> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_TIMEOUT);

    if (signal) {
      signal.addEventListener('abort', () => controller.abort());
    }

    const qs = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : '';
    try {
      const response = await fetch(`${SEARCH_API_BASE}/recents${qs}`, {
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Recents failed: ${response.status}`);
      }

      return response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  },

  /**
   * Global search across all collections
   * GET /api/search/global?q=<query>&per_page=8
   */
  global: async (
    query: string,
    options?: { per_page?: number; signal?: AbortSignal }
  ): Promise<GlobalSearchResponse> => {
    const params = new URLSearchParams({ q: query });
    if (options?.per_page) params.set('per_page', String(options.per_page));

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_TIMEOUT);

    // If caller provides a signal, abort when either fires
    if (options?.signal) {
      options.signal.addEventListener('abort', () => controller.abort());
    }

    try {
      const response = await fetch(`${SEARCH_API_BASE}/global?${params}`, {
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      return response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  },
};
